import { action } from '@ember/object';
import { service } from '@ember/service';
import type StoreService from '@ember-data/store';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { type Task, task } from 'ember-concurrency';
import { pluralize } from 'ember-inflector';
import type FloorModel from 'garaje/models/floor';
import type AjaxService from 'garaje/services/ajax';
import type FeatureConfigService from 'garaje/services/feature-config';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type MapVersionsService from 'garaje/services/map-versions';
import type MetricsService from 'garaje/services/metrics';
import type ResourcesCsvImporterService from 'garaje/services/resources-csv-importer';
import type StateService from 'garaje/services/state';
import checkType from 'garaje/utils/check-file-type';
import urlBuilder from 'garaje/utils/url-builder';

// Type for the checkType function return value
interface CheckTypeResult {
  isValid: boolean;
  extension: string;
}

// Type for resource CSV data
interface ResourceCsvData {
  envoyId: string;
  name: string;
  floorName: string;
  amenities: string;
  assignedTo: string;
}

interface ImportResourceCsvFormArgs {
  file: File | null;
  setCSVFile: (file: File | null) => void;
  records: ResourceCsvData[];
  setRecords: (records: ResourceCsvData[]) => void;
  floors: FloorModel[];
  onFileSelect: (file: File) => void;
  processing: boolean;
  disabled: boolean;
  uploadFileTask: Task<void, []>;
  resourceTypeId: string;
}

const SampleCSVWithAmenities =
  'Envoy ID,Name,Floor name,Amenities,Assigned to\n"Required. Can be numbers. Should be the unique ID of the resource","Optional. Can be the same across different floors","Required. Unique ID of letters and/or numbers","Optional. Amenities to apply on the resource.","Optional. Email of person with assigned resource, otherwise leave blank"';

export default class ImportResourceCsvForm extends Component<ImportResourceCsvFormArgs> {
  @service declare resourcesCsvImporter: ResourcesCsvImporterService;
  @service declare flashMessages: FlashMessagesService;
  @service declare featureConfig: FeatureConfigService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare metrics: MetricsService;
  @service declare state: StateService;
  @service declare store: StoreService;
  @service declare mapVersions: MapVersionsService;
  @service declare ajax: AjaxService;

  @tracked records: ResourceCsvData[] = [];

  sampleCSV = SampleCSVWithAmenities;

  @action
  clearAll(): void {
    this.args.setCSVFile(null);
    this.args.setRecords([]);
  }

  get downloadCsvUrl(): string {
    return urlBuilder.rms.downloadResourcesCsv(this.state.currentLocation.id, this.args.resourceTypeId);
  }

  get downloadTemplateUrl(): string {
    return urlBuilder.rms.downloadResourcesCsv(this.state.currentLocation.id, this.args.resourceTypeId, true);
  }

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
  parseResourcesCsvTask = task(async (file: File): Promise<void> => {
    const { setRecords, setCSVFile, records } = this.args;

    setCSVFile(file);

    const checkResult = checkType(file, ['csv']) as CheckTypeResult;
    if (checkResult.isValid) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      const result = (await this.resourcesCsvImporter.importRecordsFromCsvFile.perform(
        file,
      )) as (ResourceCsvData | null)[];
      setRecords(result.filter(Boolean) as ResourceCsvData[]);
      const wrongRows = result.length - records.length;
      if (wrongRows > 0 && records.length > 0) {
        const message = `${pluralize(wrongRows, 'row')} ${wrongRows > 1 ? 'were' : 'was'} not loaded`;
        this.flashMessages.showFlash(
          'error',
          message,
          'Please check that all rows in your file are properly formatted.',
        );
      }
    } else {
      const text = `Only files with extensions .csv are allowed. You uploaded a file with extension ${checkResult.extension}.`;
      this.flashMessages.showFlash('error', text);
    }
  });
}
